<template>
	<span>
		<b-row class="mt-3">
			<b-col cols="10">
				<h4>{{ heading }}</h4>
			</b-col>

			<b-col cols="2">
				<b-link class="float-right mb-3" @click.prevent="chooseFile" title="Upload a new document">
					<b-icon font-scale="1.5" icon="plus-square"></b-icon>
				</b-link>
			</b-col>
		</b-row>

		<b-table-simple bordered>
			<b-thead head-variant="light">
				<tr>
					<th>FileName</th>
					<th>Date</th>
					<th style="width: 12.5%"></th>
				</tr>
			</b-thead>

			<b-tbody>
				<tr v-for="(asset, index) in assetLinks" :key="index + 10">
					<td>
						<b-link href="#" @click.prevent="downloadFile(asset)"> <b-icon font-scale="1.5" :icon="getIcon(asset.fileName)"></b-icon> {{ asset.fileName }} </b-link>
					</td>

					<td>{{ asset.added }}</td>

					<td class="text-right">
						<b-button variant="link" class="float-right ml-3" @click.prevent="deleteFile(index)" title="Delete this file">
							<b-icon font-scale="1.5" icon="trash" variant="danger"></b-icon>
						</b-button>
					</td>
				</tr>
			</b-tbody>
		</b-table-simple>

		<confirm ref="confirm"></confirm>

		<input :accept="accept" type="file" ref="files" class="invisible" multiple @change.prevent="uploadFiles" />
	</span>
</template>

<script>
import FileService from '@/api-services/file.service';
import Confirm from '@/components/partials/confirm';
import { dateFuncs } from '@/globs.js';
import Vue from 'vue';
import VueImg from 'v-img';
import * as path from 'path';

Vue.use(VueImg);

const assetType = {
	0: { heading: 'Documents', accept: '.pdf, .txt, .doc, .docx, .xls, .xlsx' },
	1: { heading: 'Images', accept: 'image/*' },
	2: { heading: 'Videos', accept: 'video/*' }
};

export default {
	name: 'FileAsset',

	components: { Confirm },

	// things we have inherited from our parent
	props: ['assets', 'type', 'relate', 'folder'],

	// data we bind to, components need a function wrapping this all up, to keep instances seperate
	data: function () {
		return {
			assetLinks: [],
			files: [],
			fileToDelete: {},

			fullscreen: false,
			extensionsList: [
				{ value: 'file-post', extension: '.pdf' },
				{ value: 'file-text', extension: '.txt' },
				{ value: 'file-richtext', extension: ['.docx', '.doc'] },
				{ value: 'file-spreadsheet', extension: ['.xlsx', '.xls'] }
			]
		};
	},

	created() {
		// if we get here and already have an array, then don't wait for a watch that may never come.
		if (this.assets.length) {
			var unwatch = this.$watch('assets');
			unwatch();
			this.fetchExistingAssets();
		}
	},

	computed: {
		heading: function () {
			return assetType[this.type].heading;
		},

		accept: function () {
			return assetType[this.type].accept;
		}
	},

	// things we do to data
	methods: {
		chooseFile() {
			this.$refs.files.click();
		},
		getIcon(filename) {
			let ext = path.extname(filename);

			let icon =
				this.extensionsList.find((item) => {
					let { extension } = item;
					if (Array.isArray(extension)) {
						return extension.includes(ext);
					} else if (typeof extension === 'string') {
						return extension === ext;
					}
				}) || {};

			return icon.value || 'file-text';
		},

		// GET AND ENCODE ASSETS
		fetchExistingAssets() {
			// filter assets by type
			let asset = {};
			let assetType = this.assetType;

			let filteredAssets = this.assets.filter((asset) => {
				return asset.AssetType == this.type;
			});

			filteredAssets.forEach((asset) => {
				switch (this.type) {
					case '0': // if it's a document we can use a placeholder and stream response in download link
						this.getDocument(asset);
						break;
				}
			});
		},

		getDocument(_asset) {
			let assetLink = {
				guid: _asset.NanId,
				fileName: _asset.FileName,
				contentType: _asset.ContentType,
				added: dateFuncs.backEndDateFormat(_asset.Added),
				source: 'https://svn.alfresco.com/repos/alfresco-open-mirror/alfresco/HEAD/root/projects/repository/config/alfresco/thumbnail/thumbnail_placeholder_256_doc.png'
			};
			this.assetLinks.push(assetLink);
		},

		downloadFile(_asset) {
			FileService.get(_asset.guid)
				.then((response) => {
					const url = window.URL.createObjectURL(new Blob([response.data]));
					const link = document.createElement('a');
					link.href = url;
					link.setAttribute('download', _asset.fileName);
					document.body.appendChild(link);
					link.click();
				})
				.catch((error) => {
					this.handleError(error);
				});
		},

		/*
           UPLOAD NEW FILES
        */
		submitFiles() {
			//Initialize the form data
			let formData = new FormData();

			// Iterate over any file sent over appending the files to the form data.
			for (var i = 0; i < this.files.length; i++) {
				let file = this.files[i];
				formData.append('files[' + i + ']', file);
			}

			// add the type of file form prop
			formData.append('AssetType', this.type);
			formData.append('Folder', this.folder);

			// Make the request to the POST /multiple-files handler in api service
			FileService.upload(this.relate, formData)
				.then((res) => {
					res.data.forEach((asset, i) => {
						switch (this.type) {
							case '0': // if it's a document we can use a placeholder and stream response in download link
								this.getDocument(asset);
								break;
						}
					});

					this.$refs.confirm.open('Success', 'File(s) uploaded', true);
				})
				.catch((error) => {
					
					this.handleError(error);
				})
				.then(() => {
					this.$refs.files.value = '';
				});
		},

		uploadFiles() {
			this.files = this.$refs.files.files;
			this.submitFiles();
		},

		// Modal stuff
		deleteFile(index) {
			this.$refs.confirm.open('Delete', 'Are you sure you want to delete this file?').then((confirm) => {
				if (confirm) {
					let vm = this;

					let guid = this.assetLinks[index].guid;

					FileService.delete(this.folder, guid)
						.then(() => {
							vm.$delete(vm.assetLinks, index);
						})
						.catch((error) => {
							this.handleError(error);
						});
				}
			});
		},

		handleError(err) {
			let modal_options = {
				text: 'light',
				background: 'danger'
			};
			let message;

			if (err.response.data != '') {

				message = err.MessageDetail != null ? err.MessageDetail : err.ExceptionMessage;

			} else {

				switch (err.response.status) {
					case 404:
						message = err.response.status + ' - Could not find requested data';
						break;
					case 500:
						message = err.response.status + ' - There was a fatal error whilst processing this request';
						break;

					default:
						message = err.response.status + ' - There was an unknown error whilst processing this request';
				}
			}

			this.$refs.confirm.open('Error', message, true, modal_options);
		}
	},

	watch: {
		// Watch for the assets prop to be populated from ajax
		assets: function () {
			this.fetchExistingAssets();
		}
	}
};
</script>


// WEBPACK FOOTER //
// src/components/partials/document-assets.vue